import useAIConfigs from "@/hooks/useAIConfigs";
import useTeams from "@/hooks/useTeams";
import useRules from "@/hooks/useRules";
import React from "react";
import { RuleValue, triggerDefs, Rule } from "./types";
import { getExpressionLabel } from "@/components/Expressions/types";
import useWorkflows from "@/hooks/useWorkflows";
import useSurveys from "@/hooks/useSurveys";
import IconHistoryTime from "@/icons/IconHistoryTime";
import IconTrash from "@/icons/IconTrash";

// Map trigger keys to short category labels with colors
const TRIGGER_BADGES: Record<string, { label: string; color: string }> = {
  "use-workflow":              { label: "Workflow",       color: "bg-emerald-100 text-emerald-700" },
  "use-automation":           { label: "Automation",     color: "bg-teal-100 text-teal-700" },
  "use-ai":                   { label: "AI Bot",         color: "bg-violet-100 text-violet-700" },
  "use-prechat-survey":       { label: "Pre-Chat Survey",color: "bg-purple-100 text-purple-700" },
  "use-unavailable-survey":   { label: "Unavailable",    color: "bg-amber-100 text-amber-700" },
  "use-postchat-survey":      { label: "Post-Chat",      color: "bg-pink-100 text-pink-700" },
  "route-to-team":            { label: "Route to Team",  color: "bg-primary-bg text-primary-active" },
  "set-conversation-priority":{ label: "Priority",       color: "bg-orange-100 text-orange-700" },
  "use-workflow-on-resolve":  { label: "On Resolve",     color: "bg-sky-100 text-sky-700" },
  "use-workflow-on-unavailable":{ label: "On Unavailable",color: "bg-amber-100 text-amber-700" },
};

interface Props {
  rule: Rule;
  selected: boolean;
  onToggleSelect(): void;
  editRule(): void;
  showHistory(): void;
}

export default function RuleRowItem({ rule, selected, onToggleSelect, editRule, showHistory }: Props) {
  const { remove } = useRules();
  const { aiConfigurations } = useAIConfigs();
  const { workflows } = useWorkflows();
  const { getTeamNameById } = useTeams();
  const { surveys } = useSurveys();

  function getRuleValueLabel(key: string, value: RuleValue) {
    const result = value[key as keyof typeof rule.value] as string;
    if (!result) return "—";
    switch (rule.trigger) {
      case "use-ai":
        return aiConfigurations.find((x) => x.id === Number(result))?.name ?? result;
      case "use-workflow-on-resolve":
      case "use-workflow-on-unavailable":
        return workflows.find((x) => x.id === Number(result))?.name ?? result;
      case "use-prechat-survey":
      case "use-postchat-survey":
      case "use-unavailable-survey":
        return surveys.find((x) => x.id === Number(result))?.name ?? result;
      default:
        return result;
    }
  }

  const badge = TRIGGER_BADGES[rule.trigger] ?? { label: rule.trigger, color: "bg-slate-100 text-slate-600" };
  const targetLabel = Object.keys(rule.value).map((k) => getRuleValueLabel(k, rule.value)).join(", ");
  const conditionLabel = getExpressionLabel(rule.expression, getTeamNameById);
  const hasCondition = rule.expression && rule.expression.length > 0;

  return (
    <div
      className={`grid grid-cols-[2rem_2.5rem_14rem_1fr_auto] gap-x-4 items-center px-4 py-3 transition-colors cursor-pointer
        ${selected ? "bg-primary-bg" : "hover:bg-slate-50"}`}
      onClick={editRule}
    >
      {/* Checkbox */}
      <div
        className="flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          className="rounded border-slate-300 text-primary cursor-pointer"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Select rule ${rule.priority}`}
        />
      </div>

      {/* Priority */}
      <div className="text-center text-sm font-semibold text-slate-400 tabular-nums">
        {rule.priority}
      </div>

      {/* Trigger badge */}
      <div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.color}`}>
          {badge.label}
        </span>
      </div>

      {/* Condition + target */}
      <div className="min-w-0 text-sm">
        <span className="text-slate-700 font-medium truncate block" title={targetLabel}>
          {targetLabel || "—"}
        </span>
        <span className="text-xs text-slate-400 mt-0.5 block">
          {hasCondition ? (
            <><span className="font-semibold text-primary">WHEN</span> {conditionLabel}</>
          ) : (
            <span className="font-semibold text-slate-400">ALWAYS</span>
          )}
        </span>
      </div>

      {/* Actions */}
      <div
        className="flex items-center gap-1 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={showHistory}
          className="text-slate-300 hover:text-primary transition-colors p-1.5 rounded hover:bg-primary-bg"
          title="Version history"
          aria-label="View version history"
        >
          <IconHistoryTime width={16} height={16} />
        </button>
        <button
          onClick={() => remove(rule)}
          className="text-slate-300 hover:text-red-500 transition-colors p-1.5 rounded hover:bg-red-50"
          title="Delete rule"
          aria-label="Delete rule"
        >
          <IconTrash width={16} height={16} />
        </button>
      </div>
    </div>
  );
}
